import React, { useState, useEffect, useRef } from "react"
import { get, post, put, remove } from "../../utils/apiClient"
import toast from "react-hot-toast"
import Pagination from "../common/pagination"
import { isEmpty } from "../../utils/common"
import { MUSC_USER_STATUS, MUSC_USER_ROLES } from "../../utils/constants"
import {
  SquarePen,
  Trash2,
  Download,
  Upload,
  Siren,
  FileSpreadsheet,
} from "lucide-react"
import Select from "react-select"
// import Select from "react-select"

const arrUserStatus = [MUSC_USER_STATUS.process, MUSC_USER_STATUS.active]
const arrUserRole = [MUSC_USER_ROLES.USER, MUSC_USER_ROLES.IT]

const customSelectStyles = {
  control: (base) => ({
    ...base,
    borderRadius: 0,
    minWidth: 200,
    height: 41, // fixed height
    minHeight: 41, // ensures consistency
  }),
  valueContainer: (base) => ({
    ...base,
    justifyContent: "flex-start", // aligns selected text to the left
    paddingLeft: 8, // optional: add some breathing room
  }),
  singleValue: (base) => ({
    ...base,
    textAlign: "left", // ensures text alignment
    marginLeft: 0,
  }),
  placeholder: (base) => ({
    ...base,
    textAlign: "left", // keeps placeholder aligned too
    marginLeft: 0,
  }),
  menu: (base) => ({
    ...base,
    textAlign: "left", // ensures the whole dropdown aligns left
  }),
  option: (base, state) => ({
    ...base,
    textAlign: "left", // aligns each option text left
    backgroundColor: state.isSelected
      ? "#eee"
      : state.isFocused
      ? "#f5f5f5"
      : "white",
    color: "black",
  }),
}

const MuscUsers = () => {
  const [users, setUsers] = useState([])
  const [showPopup, setShowPopup] = useState(false)
  const [userToEdit, setUserToEdit] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [uploadLoading, setUploadLoading] = useState(false)
  const [userIdToDelete, setUserIdToDelete] = useState(null)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchText, setSearchText] = useState("")
  const [selectedStatus, setSelectedStatus] = useState(null)
  const [selectedRole, setSelectedRole] = useState(null)

  const fileInputRef = useRef(null)
  const [errorPopup, setErrorPopup] = useState(null)

  // Search Filter
  let filteredUsers =
    users?.filter((user) => {
      const matchesSearch = ["emailAddress", "firstName", "lastName"].some(
        (key) =>
          user[key]
            ?.toString()
            .toLowerCase()
            .includes(searchText?.toLowerCase().trim() || "")
      )

      const matchesStatus = selectedStatus?.value
        ? user.status === selectedStatus.value
        : true

      const matchesRole = selectedRole?.value
        ? user.netIdRole === selectedRole.value
        : true

      return matchesSearch && matchesStatus && matchesRole
    }) || []

  const startIdx = (currentPage - 1) * rowsPerPage
  const currentUsers = filteredUsers.slice(startIdx, startIdx + rowsPerPage)
  const totalPages = Math.ceil(filteredUsers.length / rowsPerPage)

  // const currentFaxes = sortedFaxes.slice(startIdx, startIdx + rowsPerPage)
  // const totalPages = Math.ceil(filteredFaxes.length / rowsPerPage)

  useEffect(() => {
    loadUser()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setCurrentPage(1)
  }, [searchText, selectedStatus, selectedRole])

  const fetchUsers = async () => {
    try {
      const data = await get("/admin/sso-customers?netIdRole=ALL")

      const mergeStatusData = data.map((v) => {
        return {
          ...v,
          status: isEmpty(v?.customerKey)
            ? MUSC_USER_STATUS.process
            : MUSC_USER_STATUS.active,
        }
      })
      setUsers(mergeStatusData)
    } catch (error) {
      console.error("Failed to fetch users", error)
    }
  }

  const loadUser = async () => {
    setLoading(true)
    await fetchUsers()
    setLoading(false)
  }

  const handleAddUser = async (data) => {
    setActionLoading(true)

    const isEdit = !!data?.emailAddress

    try {
      // if (isEdit) {
      await put(`/admin/sso-customer`, data)
      // } else {
      //   await post("/admin/sso-customer", {
      //     userName: data.userName,
      //     faxNumber: data.faxNumber,
      //   })
      // }

      fetchUsers()
      setShowPopup(false)
      setUserToEdit(null)
      toast.success(`User ${isEdit ? "updated" : "added"} successfully`)
    } catch (error) {
      console.error(`Failed to ${isEdit ? "update" : "add"} user`, error)
      toast.error(error)
    } finally {
      setActionLoading(false)
    }
  }

  const handleDeleteUser = async () => {
    setActionLoading(true)
    try {
      await remove(`/admin/sso-customer/${userIdToDelete.emailAddress}`)
      fetchUsers()
      setUserIdToDelete(null)
      toast.success("User removed successfully")
    } catch (error) {
      console.error("Failed to delete user", error)
      toast.error("Something went wrong")
    } finally {
      setActionLoading(false)
    }
  }

  const handleDownload = async () => {
    const response = await fetch("/Musc_New_Users_Upload.xlsx")
    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = "Musc_New_Users_Upload.xlsx"
    link.click()
    URL.revokeObjectURL(url)
  }

  // const handleDownload = () => {
  //   const link = document.createElement("a")
  //   link.href = `${process.env.REACT_APP_FE_URL}/Musc_New_Users_Upload.xlsx`
  //   link.download = "Musc_New_Users_Upload.xlsx"
  //   document.body.appendChild(link)
  //   link.click()
  //   document.body.removeChild(link)
  // }

  // Trigger hidden file input
  const triggerFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  // Handle file selection and upload immediately
  const handleFileChange = async (e) => {
    setUploadLoading(true)
    const file = e.target.files[0]
    if (!file) return

    try {
      const formData = new FormData()
      formData.append("file", file)

      const response = await post("/admin/upload-sso-customer", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })

      setUploadLoading(false)
      if (response.errors.length > 0) {
        setErrorPopup(response.errors)
      } else {
        toast.success(`Users imported successfully`)
        setErrorPopup(null)
      }
      fetchUsers()
    } catch (error) {
      setUploadLoading(false)
      toast.error(`Failed to upload`)
    } finally {
      setUploadLoading(false)
      e.target.value = null
    }
  }

  const handleExportCsv = () => {
    // if (user.length === 0) {
    //   toast.error("No data to export")
    //   return
    // }
    // CSV header
    // CSV header
    const headers = [
      "Email",
      "NetId",
      "First Name",
      "Last Name",
      "Role",
      "Status",
    ]
    // Build rows
    const rows = filteredUsers.map((user) => [
      user.emailAddress,
      user.netId,
      user.firstName,
      user.lastName,
      user.netIdRole,
      user.status,
    ])
    // Assemble CSV string
    const csvContent = [headers, ...rows]
      .map((row) =>
        row
          .map(
            (cell) =>
              // escape quotes
              `"${String(cell).replace(/"/g, '""')}"`
          )
          .join(",")
      )
      .join("\n")
    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `musc_users.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div style={{ display: "flex", justifyContent: "center" }}>
      {loading ? (
        <div className="loading-container-fax-list">
          <div className="loading-message">Loading...</div>
        </div>
      ) : (
        <div
          className="faxes-content"
          style={{ textAlign: "center", marginTop: "10px", width: "100%" }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              textAlign: "right",
              paddingLeft: "15px",
              fontWeight: "bold",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "right",
                marginTop: "5px",
                justifyContent: "right",
              }}
            >
              {/* <a
                  href="/Musc_New_Users_Upload.xlsx"
                  download
                  style={{ marginRight: "5px", textDecoration: "None" }}
                >
                  <Download size={14} color="blue" /> Download
                </a>
                MUSC New Users Excel Template. */}
              <span
                //className="faxes-button"
                onClick={handleDownload}
                style={{
                  marginLeft: "1px",
                  color: "blue",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                <Download size={14} color="blue" /> Download MUSC New Users
                Excel Template.
              </span>
            </div>
            <div style={{ fontSize: "12px" }}>
              <Siren size={16} color="red" /> To ensure successful upload,
              always use the provided Excel template. Do not change the header
              names or file format.
            </div>
          </div>
          <div
            className="search-container"
            style={{
              width: "100%",
              alignItems: "end",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", gap: "5px" }}>
              <div style={{ paddingRight: "10x" }}>
                <input
                  type="text"
                  placeholder={`Search Users`}
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value)
                    setCurrentPage(1)
                  }}
                />
              </div>

              <div>
                <Select
                  className="basic-single"
                  classNamePrefix="select"
                  placeholder="Filter by Status"
                  options={arrUserStatus.map((n) => ({
                    value: n,
                    label: n,
                  }))}
                  value={selectedStatus}
                  onChange={setSelectedStatus}
                  isClearable
                  isSearchable
                  styles={customSelectStyles}
                />
              </div>

              <div>
                <Select
                  className="basic-single"
                  classNamePrefix="select"
                  placeholder="Filter by Role"
                  options={arrUserRole.map((n) => ({
                    value: n,
                    label: n,
                  }))}
                  value={selectedRole}
                  onChange={setSelectedRole}
                  isClearable
                  isSearchable
                  styles={customSelectStyles}
                />
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  paddingLeft: "10px",
                  paddingRight: "10px",
                }}
                onClick={() => {
                  setSearchText("")
                  setSelectedStatus(null)
                  setSelectedRole(null)
                }}
                title="Reset"
              >
                {" "}
                ↺
              </div>
            </div>

            <div style={{ display: "flex" }}>
              {/* Upload Button */}

              <button
                className="faxes-button"
                onClick={handleExportCsv}
                style={{ marginTop: "12px", height: "28px" }}
              >
                <span>
                  <FileSpreadsheet size={16} color="white" />
                </span>
                <span>Export Users</span>
              </button>

              <button
                className="faxes-button"
                onClick={triggerFileSelect}
                style={{ marginTop: "12px" }}
              >
                <Upload size={16} color="white" />
                {uploadLoading ? `Uploading ...` : `Upload Excel`}
              </button>
              <input
                type="file"
                accept=".xlsx,.xls"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              {/* <button
                className="faxes-button"
                onClick={() => setShowPopup(true)}
                style={{ marginLeft: "30px" }}
              >
                <Plus size={16} color="white" /> Add User
              </button> */}
              {/* Hidden file input */}
            </div>
          </div>
          <div className="table-container">
            <table className="faxes-table">
              <thead>
                <tr>
                  <th className="faxes-th">Email</th>
                  <th className="faxes-th">NetId</th>
                  <th className="faxes-th">First Name</th>
                  <th className="faxes-th">Last Name</th>
                  <th className="faxes-th">Role</th>
                  <th className="faxes-th">Status</th>

                  {/* <th className="faxes-th">Secret</th> */}
                  <th
                    className="faxes-th"
                    style={{ textAlign: "center", width: "100px" }}
                  >
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentUsers.length > 0 ? (
                  currentUsers.map((user) => (
                    <tr
                      key={user.userId}
                      className="fax-item"
                      style={{ cursor: "unset" }}
                    >
                      <td className="faxes-td">{user.emailAddress}</td>
                      <td className="faxes-td">{user.netId}</td>
                      <td className="faxes-td">{user.firstName}</td>
                      <td className="faxes-td">{user.lastName}</td>
                      <td className="faxes-td">{user.netIdRole}</td>
                      <td className="faxes-td">{user.status}</td>

                      <td className="faxes-td" style={{ textAlign: "center" }}>
                        <span
                          style={{ cursor: "pointer" }}
                          onClick={() => setUserToEdit(user)}
                        >
                          <SquarePen size="18" color="black" />
                        </span>
                        <span
                          style={{ cursor: "pointer", marginLeft: "10px" }}
                          onClick={() => setUserIdToDelete(user)}
                        >
                          <Trash2 size="18" color="red" />
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="100%"
                      style={{
                        textAlign: "center",
                        padding: "10px",
                        border: "1px solid #ccc",
                      }}
                    >
                      No data found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {filteredUsers.length > 0 && (
            <div
              className="faxes-pagination"
              style={{
                marginTop: "8px",
                display: "flex",
                justifyContent: "end",
              }}
            >
              {filteredUsers.length > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  rowsPerPage={rowsPerPage}
                  totalRows={filteredUsers.length}
                  onPageChange={(page) => setCurrentPage(page)}
                  onRowsPerPageChange={(count) => {
                    setRowsPerPage(count)
                    setCurrentPage(1) // Reset to first page on change
                  }}
                />
              )}
            </div>
          )}
        </div>
      )}

      {(showPopup || userToEdit) && (
        <UserForm
          onSave={handleAddUser}
          onClose={() => {
            setShowPopup(false)
            setUserToEdit(null)
          }}
          userToEdit={userToEdit}
          actionLoading={actionLoading}
        />
      )}

      {userIdToDelete && (
        <DeleteConfirmationPopup
          onConfirm={handleDeleteUser}
          onCancel={() => setUserIdToDelete(null)}
          actionLoading={actionLoading}
          userIdToDelete={userIdToDelete}
        />
      )}

      {errorPopup && (
        <ErrorPopup
          errors={errorPopup}
          onClose={() => {
            setErrorPopup(null)
          }}
        />
      )}
    </div>
  )
}

const UserForm = ({ onClose, onSave, userToEdit, actionLoading }) => {
  const [errorMessage, setErrorMessage] = useState("")
  const [selectedRole, setSelectedRole] = useState("USER")

  // Load initial data if editing
  useEffect(() => {
    if (userToEdit) {
      setSelectedRole(userToEdit?.netIdRole)
    }
  }, [userToEdit])

  const handleRoleChange = (role) => {
    //const role = e.target.value
    setSelectedRole(role)
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!selectedRole.trim()) {
      setErrorMessage("Role is required.")
      return
    }

    if (userToEdit?.netIdRole === selectedRole) {
      setErrorMessage("Please change the role")
      return
    }

    onSave({
      emailAddress: userToEdit?.emailAddress || null,
      oldNetIdRole: userToEdit?.netIdRole,
      newNetIdRole: selectedRole,
    })
  }

  return (
    <div className="popup-overlay">
      <div className="popup">
        <div className="checkbox-popup-header">
          <h3 className="popup-title">
            {userToEdit ? `Update ${userToEdit.emailAddress}` : `Add`}
          </h3>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              fontSize: 24,
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>

        <form className="popup-group" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="userName">
              Role: <span className="mandatory">*</span>
            </label>

            <select
              id="rowsPerPage"
              value={selectedRole}
              onChange={(e) => handleRoleChange(e.target.value)}
              style={{
                padding: "5px",
                // fontSize: "12px",
                outline: "none",
                // border: "1px solid #ccc",
              }}
            >
              {arrUserRole.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
            {/* <Select
              id="roleSelect"
              className="basic-single"
              classNamePrefix="select"
              value={optionRoles.find(
                (option) => option.value === selectedRole
              )}
              // onChange={handleRoleChange}
              onChange={(selectedOption) =>
                handleRoleChange(selectedOption.value)
              }
              options={optionRoles}
              styles={{
                control: (base) => ({
                  ...base,
                  borderRadius: 0,
                  maxWidth: "200px",
                }),
              }}
              // isSearchable
            /> */}
          </div>

          {errorMessage && <div className="error-message">{errorMessage}</div>}

          <div className="popup-buttons">
            <button
              className="popup-button cancel"
              type="button"
              onClick={onClose}
            >
              Cancel
            </button>
            <button className="popup-button save" type="submit">
              {actionLoading ? `Saving ...` : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const DeleteConfirmationPopup = ({
  onConfirm,
  onCancel,
  actionLoading,
  userIdToDelete,
}) => (
  <div className="popup-overlay">
    <div className="popup" style={{ padding: "20px", maxWidth: "700px" }}>
      {actionLoading ? (
        <div>Processing...</div>
      ) : (
        <>
          {/* <h3>Confirm Deletion</h3> */}
          <p>
            Are you sure you want to delete the user{" "}
            <strong>{userIdToDelete?.emailAddress}?</strong>
          </p>
          <p>
            ⚠️ This will permanently remove the user and all associated folders,
            copied faxes, and access records. Once deleted, it cannot be
            recovered.
          </p>
          <div className="popup-buttons">
            <button className="cancel" onClick={onCancel}>
              Cancel
            </button>
            <button className="delete" onClick={onConfirm}>
              Delete
            </button>
          </div>
        </>
      )}
    </div>
  </div>
)

const ErrorPopup = ({ onClose, errors }) => {
  return (
    <div className="popup-overlay">
      <div className="popup">
        <div className="checkbox-popup-header">
          <h3 className="popup-title" style={{ color: "red" }}>
            Upload Errors
          </h3>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              fontSize: 24,
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>
        <div
          className="table-container"
          style={{
            padding: "18px",
            width: "410px",
            maxHeight: "400px",
            overflowX: "auto",
          }}
        >
          <table className="faxes-table">
            <thead>
              <tr>
                <th className="faxes-th" style={{ textAlign: "center" }}>
                  No
                </th>
                <th className="faxes-th">Failure Reason</th>
              </tr>
            </thead>
            <tbody>
              {errors.length > 0 ? (
                errors.map((error, i) => (
                  <tr key={i} className="fax-item" style={{ cursor: "unset" }}>
                    <td className="faxes-td" style={{ textAlign: "center" }}>
                      {i + 1}
                    </td>
                    <td className="faxes-td">{error}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="100%"
                    style={{
                      textAlign: "center",
                      padding: "10px",
                      border: "1px solid #ccc",
                    }}
                  >
                    No data found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default MuscUsers
